import { motion } from "framer-motion";
import { Accordion, Input, Text, StatusChip } from "@fragments";
import { ReactComponent as GinasteeCard } from "@assets/draws/card-ginastee.svg";
import * as Mui from "@mui/material";
import {
    IconInfoCircle,
    IconSquareRoundedMinus,
    IconChecks,
    IconRefresh,
    IconAlertTriangle,
} from "@tabler/icons-react";
import Parser from "@helpers/parser";
import DocumentRow from "./components/DocumentRow";
import "./styles.scss";

const ANALYSIS_STATUS_CONFIG = {
    pending: { text: "Aguardando", variant: "default", Icon: IconSquareRoundedMinus },
    rejected: { text: "Reprovado", variant: "error", Icon: IconAlertTriangle },
    completed: { text: "Concluído", variant: "success", Icon: IconChecks },
    approved: { text: "Aprovado", variant: "success", Icon: IconChecks },
    active: { text: "Ativo", variant: "success", Icon: IconChecks },
    awaiting_approval: { text: "Em análise", variant: "info", Icon: IconRefresh },
};

function AnalysisStatusRow({ label, status }) {
    const config = ANALYSIS_STATUS_CONFIG[status] || ANALYSIS_STATUS_CONFIG.pending;
    const { Icon } = config;
    return (
        <div className="analysis-status-row">
            <StatusChip
                variant={config.variant}
                text={config.text}
                icon={
                    <Icon
                        className={status === 'awaiting_approval' ? 'analysis-spin' : ''}
                        size={16}
                    />
                }
            />
            <Text.h5 contrast={7}>{label}</Text.h5>
        </div>
    );
}

export default function BankOnboardingView({
    // Form
    getName, setGetName,
    getPhone, setGetPhone,
    getAddress, setGetAddress,
    getLegalType, setGetLegalType,
    getDocument, setGetDocument,
    getBirthDate, setGetBirthDate,
    getCompanyType, setGetCompanyType,
    getIncome, setGetIncome,
    getPixKeyType, setGetPixKeyType,
    getPixKey, setGetPixKey,
    // API / status
    account,
    loadingWalletStatus,
    submitting,
    errors,
    creatingProgress,
    walletCreated,
    walletInProgress,
    // Accordion control
    currentStep, setCurrentStep,
    completedSteps,
    // Validators
    validateStep1,
    validateStep2,
    validateStep3,
    validateStep4,
    // Actions
    handleDocumentUpload,
    handleRefreshStatus,
}) {
    const documents = account?.provider?.pending_documents || [];
    const statuses = account?.statuses || {};

    const docsFinished = account?.id && (documents.length === 0 || documents.every(doc => doc.status?.toLowerCase() === 'approved'));

    const analysisCompleted =
        statuses.general === 'approved' || statuses.general === 'active';

    // Steps 4–5 unlock once the wallet exists or creation is underway
    const step4Disabled = (!walletCreated && !walletInProgress) || statuses.documentation === 'awaiting_approval';
    const step5Disabled = !walletCreated;

    return (
        <motion.div
            className="bank-onboarding"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
        >
            {/* ── Header ─────────────────────────────────────────────── */}
            <div className="bank-onboarding-header pt-3">
                <GinasteeCard className="bank-onboarding-card-img" />
                <div>
                    <Text.h2 weight={600}>Ginastee Wallet</Text.h2>
                    <Text.h5 contrast={5} weight={300} className="mt-1">
                        Cadastre sua conta de recebíveis para receber pagamentos no Ginastee
                    </Text.h5>
                </div>
            </div>

            {/* ── Accordion group ────────────────────────────────────── */}
            <div className="accordion-group mt-6 mb-6">

                {/* Step 1 – personal info */}
                <Accordion
                    title="#01 • Suas informações"
                    stepTitle="Informações pessoais"
                    stepDescription="Precisamos de algumas informações essenciais para abrir a sua conta."
                    summary={completedSteps[1] ? `${getName} • ${Parser.phone(getPhone)}` : null}
                    isOpen={!loadingWalletStatus && currentStep === 1}
                    isCompleted={completedSteps[1]}
                    onToggle={() => setCurrentStep(currentStep === 1 ? null : 1)}
                    onNext={validateStep1}
                    disabled={loadingWalletStatus || walletCreated || walletInProgress}
                    loading={loadingWalletStatus}
                >
                    <Input.FullName
                        placeholder="Nome do titular"
                        value={getName}
                        onInput={setGetName}
                        autoFocus
                    />
                    <Input.Phone
                        value={getPhone}
                        onInput={setGetPhone}
                        label="Celular"
                    />
                </Accordion>

                {/* Step 2 – address */}
                <Accordion
                    title="#02 • Endereço de cobrança"
                    stepTitle="Preencha o endereço fiscal"
                    stepDescription="Seu endereço é usado para fins de conformidade da sua conta cadastrada na receita federal."
                    summary={completedSteps[2] && getAddress ? `${getAddress.street}, ${getAddress.number}${getAddress.complement ? ` - ${getAddress.complement}` : ''} - ${getAddress.neighborhood}, ${getAddress.city} - ${getAddress.state}, CEP: ${getAddress.zipcode?.replace(/^(\d{5})(\d{3})$/, "$1-$2")}` : null}
                    isOpen={!loadingWalletStatus && currentStep === 2}
                    isCompleted={completedSteps[2]}
                    onToggle={() => setCurrentStep(currentStep === 2 ? null : 2)}
                    onNext={validateStep2}
                    disabled={loadingWalletStatus || !completedSteps[1] || walletCreated || walletInProgress}
                    loading={loadingWalletStatus}
                >
                    <Input.Address
                        value={getAddress}
                        onInput={setGetAddress}
                    />
                </Accordion>

                {/* Step 3 – accounting info */}
                <Accordion
                    title="#03 • Informações contábeis"
                    stepTitle="Preencha as informações contábeis"
                    stepDescription="Estas informações são utilizadas para cadastro da conta de recebíveis na processadora de pagamentos."
                    summary={completedSteps[3] || walletCreated ? (getLegalType === 'physical' ? `Pessoa Física • ${Parser.cpf(getDocument)} • ${Parser.date.ddmmyyyy(getBirthDate)}` : `Pessoa Jurídica • ${Parser.cnpj(getDocument)}`) : null}
                    isOpen={!loadingWalletStatus && currentStep === 3}
                    isCompleted={completedSteps[3] || walletCreated}
                    onToggle={() => setCurrentStep(currentStep === 3 ? null : 3)}
                    onNext={validateStep3}
                    disabled={loadingWalletStatus || !completedSteps[2] || walletCreated || walletInProgress}
                    loading={loadingWalletStatus || submitting}
                >
                    <Input.Choices
                        label="Tipo de conta"
                        options={[
                            { value: 'physical', label: 'Pessoa física' },
                            { value: 'juridical', label: 'Pessoa jurídica' },
                        ]}
                        value={getLegalType}
                        onInput={setGetLegalType}
                    />

                    {getLegalType === 'physical' ? (
                        <>
                            <Input.CPF
                                value={getDocument}
                                onInput={setGetDocument}
                                label="CPF"
                            />
                            <Input.Date
                                label="Data de nascimento"
                                value={getBirthDate}
                                onInput={setGetBirthDate}
                            />
                        </>
                    ) : (
                        <>
                            <Input.Select
                                label="Enquadramento da empresa"
                                placeholder="Selecione a categoria"
                                options={[
                                    { label: "MEI", value: "MEI" },
                                    { label: "Limitada (LTDA)", value: "LIMITED" },
                                    { label: "Individual", value: "INDIVIDUAL" },
                                    { label: "Associação", value: "ASSOCIATION" },
                                ]}
                                value={getCompanyType}
                                onInput={setGetCompanyType}
                            />
                            <Input.CNPJ
                                value={getDocument}
                                onInput={setGetDocument}
                                label="CNPJ"
                            />
                        </>
                    )}

                    <Input.Money
                        value={getIncome}
                        onInput={setGetIncome}
                        label={getLegalType === 'physical' ? "Renda mensal" : "Faturamento mensal"}
                    />

                    <Text.h4 className="mt-3" weight={400}>Chave pix para recebíveis</Text.h4>

                    <Input.Select
                        label="Tipo de chave Pix"
                        placeholder="Selecione o tipo de chave"
                        options={[
                            { label: "CPF", value: "CPF" },
                            { label: "CNPJ", value: "CNPJ" },
                            { label: "E-mail", value: "EMAIL" },
                            { label: "Celular", value: "PHONE" },
                            { label: "Chave Aleatória", value: "EVP" },
                        ]}
                        value={getPixKeyType}
                        onInput={(val) => {
                            setGetPixKeyType(val);
                            setGetPixKey('');
                        }}
                    />

                    {(() => {
                        const baseProps = {
                            value: getPixKey,
                            onInput: setGetPixKey,
                            label: "Chave Pix de recebimento",
                        };

                        switch (getPixKeyType) {
                            case 'CPF':
                                return <Input.CPF {...baseProps} placeholder="000.000.000-00" />;
                            case 'CNPJ':
                                return <Input.CNPJ {...baseProps} placeholder="00.000.000/0000-00" />;
                            case 'EMAIL':
                                return <Input.Text {...baseProps} placeholder="email@exemplo.com" type="email" />;
                            case 'PHONE':
                                return <Input.Phone {...baseProps} />;
                            case 'EVP':
                            default:
                                return <Input.Text {...baseProps} placeholder="Chave aleatória" />;
                        }
                    })()}

                    <Text.span contrast={5} left={<IconInfoCircle />}>
                        É necessário informar o faturamento médio mensal, por exigência da receita federal.
                    </Text.span>

                    {errors && (
                        <Mui.Alert severity="error" className="mt-2">
                            {errors}
                        </Mui.Alert>
                    )}
                </Accordion>

                {/* Step 4 – documents */}
                <Accordion
                    title="#04 • Validação de identidade"
                    stepTitle="Envie os documentos solicitados"
                    stepDescription="Para que sua conta seja aprovada, envie os documentos abaixo. Formatos aceitos: PNG, JPG ou PDF (máx. 20 MB)."
                    isOpen={currentStep === 4}
                    isCompleted={docsFinished}
                    onToggle={() => setCurrentStep(currentStep === 4 ? null : 4)}
                    onNext={validateStep4}
                    disabled={step4Disabled || docsFinished || (!completedSteps[3] && !walletCreated)}
                    loading={loadingWalletStatus}
                >
                    {walletInProgress ? (
                        <div className="wallet-creating-state">
                            <Text.h5 contrast={6} className="mb-3">
                                Sua conta está sendo criada, aguarde alguns instantes…
                            </Text.h5>
                            <Mui.LinearProgress
                                variant="determinate"
                                value={((18 - creatingProgress) / 18) * 100}
                                className="wallet-creating-progress-bar"
                            />
                            {creatingProgress > 0 && (
                                <Text.span contrast={5} className="mt-2">
                                    {creatingProgress} segundo{creatingProgress !== 1 ? 's' : ''} restante{creatingProgress !== 1 ? 's' : ''}
                                </Text.span>
                            )}
                        </div>
                    ) : (
                        <div className="documents-list">
                            {documents.length === 0 ? (
                                <Text.h5 contrast={5}>Nenhum documento solicitado ainda.</Text.h5>
                            ) : (
                                documents.map((doc, i) => (
                                    <DocumentRow
                                        key={doc.id ?? i}
                                        document={doc}
                                        onUpload={handleDocumentUpload}
                                        onRefresh={handleRefreshStatus}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </Accordion>

                {/* Step 5 – analysis (read-only, no onNext) */}
                <Accordion
                    title="#05 • Análise"
                    stepTitle="Acompanhe a análise da sua conta"
                    stepDescription="As informações serão analisadas em até 48h após o envio dos documentos."
                    isOpen={currentStep === 5}
                    isCompleted={analysisCompleted}
                    onToggle={() => setCurrentStep(currentStep === 5 ? null : 5)}
                    disabled={step5Disabled}
                    loading={loadingWalletStatus}
                >
                    <div className="analysis-status-list">
                        <AnalysisStatusRow
                            label="Validação da conta cadastrada"
                            status={statuses.commercial}
                        />
                        <AnalysisStatusRow
                            label="Validação dos documentos"
                            status={statuses.documentation}
                        />
                        <AnalysisStatusRow
                            label="Confirmação geral do cadastro"
                            status={statuses.general}
                        />
                    </div>
                </Accordion>

            </div>
        </motion.div>
    );
}
